import { ExpenseWorkflowStatusEnum } from './expense'

/**
 * Who may edit an expense, and which parts. Shared by the API (which
 * enforces it) and the clients (which hide or lock Edit), so a user is never
 * offered an edit the server refuses.
 *
 * - Members edit only their own expenses, and never once APPROVED or
 *   CANCELED (an approver has to send it back first).
 * - Expense managers (EXPENSE:MANAGE) may edit any expense in any status.
 * - For everyone, managers included, the money facts are frozen while the
 *   expense is billed on a live client invoice or has been reimbursed:
 *   amount, currency, date, kind, billable flag and linked context. Notes,
 *   category, payment method and receipts stay editable.
 */

/** Fields frozen while an expense is billed or reimbursed. */
export const EXPENSE_FINANCIAL_FIELDS = [
  'subtotal',
  'taxAmount',
  'tipAmount',
  'totalAmount',
  'currency',
  'expenseDate',
  'kind',
  'billable',
  'links',
] as const

export type ExpenseFinancialField = (typeof EXPENSE_FINANCIAL_FIELDS)[number]

export interface ExpenseEditPolicyInput {
  workflowStatus: ExpenseWorkflowStatusEnum | `${ExpenseWorkflowStatusEnum}`
  reimbursedAmount?: number | string | null
  /** The live client invoice billing this expense, if any. */
  billedInvoice?: { invoiceNumber?: string | null } | null
  /** The viewer owns the expense. */
  isOwner: boolean
  /** The viewer holds EXPENSE:MANAGE. */
  canManageAll: boolean
}

export interface ExpenseEditPolicy {
  /** The edit form may be opened. */
  canEdit: boolean
  /** Amount, currency, date, kind, billable flag and context may change. */
  canEditFinancials: boolean
  /** Why editing is blocked or partly locked; null when fully editable. */
  reason: string | null
}

export function getExpenseEditPolicy(
  input: ExpenseEditPolicyInput,
): ExpenseEditPolicy {
  if (!input.canManageAll) {
    if (!input.isOwner) {
      return {
        canEdit: false,
        canEditFinancials: false,
        reason: 'You can only edit your own expenses.',
      }
    }
    if (input.workflowStatus === ExpenseWorkflowStatusEnum.APPROVED) {
      return {
        canEdit: false,
        canEditFinancials: false,
        reason:
          'This expense is approved, so it can no longer be changed. Ask an expense manager if something needs fixing.',
      }
    }
    if (input.workflowStatus === ExpenseWorkflowStatusEnum.CANCELED) {
      return {
        canEdit: false,
        canEditFinancials: false,
        reason: 'This expense was canceled and can no longer be changed.',
      }
    }
  }

  const financialsLock = getExpenseFinancialsLockReason(input)
  return {
    canEdit: true,
    canEditFinancials: financialsLock === null,
    reason: financialsLock,
  }
}

/**
 * Why the money facts of an expense are frozen, or null when they are not.
 * Applies to every role.
 */
export function getExpenseFinancialsLockReason(
  input: Pick<ExpenseEditPolicyInput, 'billedInvoice' | 'reimbursedAmount'>,
): string | null {
  if (input.billedInvoice) {
    const label = input.billedInvoice.invoiceNumber
      ? `invoice ${input.billedInvoice.invoiceNumber}`
      : 'a client invoice'
    return `This expense is billed on ${label}. Remove it from the invoice first (delete the invoice while it is a draft, or void it) before changing the amount, currency, date, kind, billable flag, or linked record.`
  }
  if (Math.round(Number(input.reimbursedAmount ?? 0) * 100) > 0) {
    return 'This expense has been reimbursed. Reverse the reimbursement before changing the amount, currency, date, kind, billable flag, or linked record.'
  }
  return null
}
